import React, { useCallback, useEffect, useState } from "react";
import { getLocale } from "../../../../i18n";
import { useTranslation } from "../../../../i18n/useTranslation";
import { AuditService } from "../../../../services/audit.service";
import Loading from "../../../components/Loading";
import "./index.scss";

type AuditItem = {
  id: string;
  action: string;
  actorUsername: string;
  targetType: string | null;
  targetId: string | null;
  meta: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(getLocale() === "en" ? "en-US" : "vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour12: false,
  });
};

const AuditLog: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<AuditItem[]>([]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await AuditService.list({ page: 1, limit: 50 });
      setItems(res.data?.data?.items || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const describeAction = (item: AuditItem) => {
    const key = `audit.actions.${item.action}`;
    const translated = t(key);
    if (translated !== key) {
      return translated;
    }
    return item.action;
  };

  const describeTarget = (item: AuditItem) => {
    if (item.targetType === "device" && item.targetId) {
      return `${t("audit.device")}: ${item.targetId}`;
    }
    if (item.meta?.username) {
      return String(item.meta.username);
    }
    return item.targetId || "-";
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="audit-log">
      <div className="audit-log__header">
        <h3>{t("audit.title")}</h3>
        <button type="button" className="audit-log__refresh" onClick={fetchLogs}>
          {t("common.refresh")}
        </button>
      </div>
      <div className="audit-log__table">
        <div className="audit-log__row audit-log__row--head">
          <div>{t("audit.time")}</div>
          <div>{t("audit.action")}</div>
          <div>{t("audit.actor")}</div>
          <div>{t("audit.target")}</div>
          <div>IP</div>
        </div>
        {items.length === 0 ? (
          <div className="audit-log__empty">{t("audit.empty")}</div>
        ) : (
          items.map((item) => (
            <div className="audit-log__row" key={item.id}>
              <div>{formatDateTime(item.createdAt)}</div>
              <div>{describeAction(item)}</div>
              <div>{item.actorUsername || "-"}</div>
              <div>{describeTarget(item)}</div>
              <div>{item.ip || "-"}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AuditLog;
